import { BaseFirestoreService } from "../FirestoreService";
import { USER_PRIVATE_COLLECTION, USER_PRIVATE_FINANCE_DOCUMENT, USER_PRIVATE_INFO_DOCUMENT, USER_PRIVATE_LOGIN_DOCUMENT, USER_PRIVATE_NOTIFICATIONS_DOCUMENT, USER_PRIVATE_PREFERENCES_DOCUMENT, USER_PRIVATE_PRIVACY_DOCUMENT, USER_PRIVATE_REFERRALS_DOCUMENT, UserPrivateFinances, UserPrivateInfo, UserPrivateLogin, UserPrivateNotifications, UserPrivatePreferences, UserPrivatePrivacy, UserPrivateReferrals, USERS_COLLECTION } from "../../db/user";
import { FirestoreDatabase as Database, FirestoreDocumentReference } from "../../db/common";

/** Each document in the private collection has a specific id (not random) */
export type InfoTypeDocId =
    | typeof USER_PRIVATE_INFO_DOCUMENT
    | typeof USER_PRIVATE_LOGIN_DOCUMENT
    | typeof USER_PRIVATE_FINANCE_DOCUMENT
    | typeof USER_PRIVATE_NOTIFICATIONS_DOCUMENT
    | typeof USER_PRIVATE_PRIVACY_DOCUMENT
    | typeof USER_PRIVATE_PREFERENCES_DOCUMENT
    | typeof USER_PRIVATE_REFERRALS_DOCUMENT

/** Each document in the private collection has its specific type */
export type InfoTypeDoc =
    | UserPrivateInfo
    | UserPrivateLogin
    | UserPrivateFinances
    | UserPrivateNotifications
    | UserPrivatePrivacy
    | UserPrivatePreferences
    | UserPrivateReferrals

// Mapping between document IDs and their types
export type InfoTypeMap = {
    [USER_PRIVATE_INFO_DOCUMENT]: UserPrivateInfo;
    [USER_PRIVATE_LOGIN_DOCUMENT]: UserPrivateLogin;
    [USER_PRIVATE_FINANCE_DOCUMENT]: UserPrivateFinances;
    [USER_PRIVATE_NOTIFICATIONS_DOCUMENT]: UserPrivateNotifications;
    [USER_PRIVATE_PRIVACY_DOCUMENT]: UserPrivatePrivacy;
    [USER_PRIVATE_PREFERENCES_DOCUMENT]: UserPrivatePreferences;
    [USER_PRIVATE_REFERRALS_DOCUMENT]: UserPrivateReferrals;
    [key: string]: InfoTypeDoc;
}

/**
 * Shared User Private informations Service
 * All the document under the private collection
 * Works with both Firebase Admin SDK (backend) and Client SDK (frontend)
 */
export class UserPrivateInfosService extends BaseFirestoreService<InfoTypeDoc> {
    constructor(protected db: Database, protected userId: string) {
        super(db);
    }
    protected getCollectionPath(): string {
        return `${USERS_COLLECTION}/${this.userId}/${USER_PRIVATE_COLLECTION}`;
    }

    //@overwrite just to specify the type
    public getDocumentRef(id: InfoTypeDocId): FirestoreDocumentReference {
        return super.getDocumentRef(id)
    }

    get<T extends InfoTypeDocId>(id: T): Promise<InfoTypeMap[T] | null> {
        return super.get(id) as Promise<InfoTypeMap[T] | null>;
    }

    create<T extends InfoTypeDocId>(data: InfoTypeMap[T], id: T): Promise<T> {
        return super.create(data, id) as Promise<T>;
    }

    update<T extends InfoTypeDocId>(id: T, data: Partial<InfoTypeMap[T]>): Promise<void> {
        return super.update(id, data)
    }

    merge<T extends InfoTypeDocId>(id: T, data: Partial<InfoTypeMap[T]>): Promise<void> {
        return super.merge(id, data)
    }

    delete<T extends InfoTypeDocId>(id: T): Promise<void> {
        return super.delete(id)
    }

    batchCreate<T extends InfoTypeDocId>(entities: { id?: T; data: InfoTypeMap[T]; }[]): Promise<T[]> {
        return super.batchCreate(entities) as Promise<T[]>;
    }

    batchUpdate<T extends InfoTypeDocId>(updates: { id: T; data: Partial<InfoTypeMap[T]>; }[]): Promise<void> {
        return super.batchUpdate(updates)
    }

    batchMerge<T extends InfoTypeDocId>(updates: { id: T; data: Partial<InfoTypeMap[T]>; }[]): Promise<void> {
        return super.batchMerge(updates);
    }

    batchDelete<T extends InfoTypeDocId>(ids: T[]): Promise<void> {
        return super.batchDelete(ids)
    }
}
